/**
 * Gyroscope parallax - elements move when the phone tilts.
 * Uses DeviceOrientation API for mobile depth effect.
 * Handles iOS 13+ permission request.
 */

export function initGyroscope(root: HTMLElement): () => void {
  if (!('DeviceOrientationEvent' in window)) return () => {};
  if (!('ontouchstart' in window)) return () => {};

  const layers = root.querySelectorAll<HTMLElement>('[data-parallax-depth]');
  if (layers.length === 0) return () => {};

  let baseGamma = 0;
  let baseBeta = 0;
  let calibrated = false;
  let listening = false;

  function onOrientation(e: DeviceOrientationEvent) {
    const gamma = e.gamma ?? 0;
    const beta = e.beta ?? 0;

    if (!calibrated) {
      baseGamma = gamma;
      baseBeta = beta;
      calibrated = true;
      return;
    }

    const dx = (gamma - baseGamma) * 0.8;
    const dy = (beta - baseBeta) * 0.5;

    for (const layer of layers) {
      const depth = Number(layer.dataset.parallaxDepth ?? 1);
      const tx = dx * depth;
      const ty = dy * depth;
      layer.style.transform = `translate3d(${tx}px, ${ty}px, 0)`;
    }
  }

  function startListening() {
    if (listening) return;
    listening = true;
    window.addEventListener('deviceorientation', onOrientation);
  }

  // iOS 13+ requires permission request from user gesture
  const DOE = DeviceOrientationEvent as unknown as {
    requestPermission?: () => Promise<string>;
  };

  if (typeof DOE.requestPermission === 'function') {
    const handler = () => {
      DOE.requestPermission?.().then((state) => {
        if (state === 'granted') startListening();
      });
      document.removeEventListener('touchstart', handler);
    };
    document.addEventListener('touchstart', handler, { once: true });
  } else {
    startListening();
  }

  return () => {
    window.removeEventListener('deviceorientation', onOrientation);
    for (const layer of layers) {
      layer.style.transform = '';
    }
  };
}
